import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TOOL_TYPES } from '../../store/useToolStore';
import useThemeStore from '../../store/useThemeStore';
import { TOOL_SLUGS } from '../../data/toolSlugs';

const TOOL_NAMES = {
  [TOOL_TYPES.LOADER]: 'Loaders',
  [TOOL_TYPES.GRADIENT]: 'Gradients',
  [TOOL_TYPES.CARD]: 'Cards',
  [TOOL_TYPES.ANIMATION]: 'Animations',
  [TOOL_TYPES.ALERT]: 'Alerts',
  [TOOL_TYPES.PHOTO_PICKER]: 'Photo Picker',
  [TOOL_TYPES.JSON_TO_MODEL]: 'JSON to Model',
  [TOOL_TYPES.API_GENERATOR]: 'API Generator',
  [TOOL_TYPES.STORYBOARD_CONVERTER]: 'Storyboard Converter',
  [TOOL_TYPES.POSTMAN_IMPORTER]: 'Postman Importer',
  [TOOL_TYPES.SHADOW]: 'Shadows',
  [TOOL_TYPES.BUTTON]: 'Buttons',
  [TOOL_TYPES.GRID]: 'Grids',
  [TOOL_TYPES.PALETTE]: 'Color Palettes',
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navTools = TOOL_SLUGS.filter((t) => t.toolType !== TOOL_TYPES.STORYBOARD_CONVERTER);

  return (
    <header className="relative isolate bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 z-50">
      <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src={theme === 'dark' ? '/light-logo.png' : '/dark-logo.png'} 
              alt="SwiftToolsKit" 
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Home
            </Link>
            <div className="relative group">
              <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                TOOL_SLUGS.some((t) => location.pathname === t.seoPath || location.pathname === t.legacyPath)
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}>
                Tools
                <svg className="inline-block ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute left-0 z-[60] mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="p-2">
                  {/* Tools List */}
                  <div className="py-2 max-h-96 overflow-y-auto">
                    {navTools.map((t) => (
                      <Link
                        key={t.toolType}
                        to={t.seoPath}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          isActive(t.seoPath) ? 'bg-gray-100 dark:bg-gray-700 text-primary-600 dark:text-primary-500' : ''
                        }`}
                      >
                        {TOOL_NAMES[t.toolType] || t.toolType}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/about') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              About Us
            </Link>
            <Link
              to="/contact"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/contact') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Contact Us
            </Link>
            <Link
              to="/blog"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/blog') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Blog
            </Link>
          </div>

          {/* Right side buttons */}
          <div className="flex items-center space-x-2">
            {/* Theme toggle button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 dark:border-gray-800">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2 rounded-lg text-sm font-medium mb-2 ${
                isActive('/') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Home
            </Link>
            <div className="mb-2">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-1">
                Tools
              </div>
              <div className="space-y-1">
                {navTools.map((t) => (
                  <Link
                    key={t.toolType}
                    to={t.seoPath}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium ml-4 ${
                      isActive(t.seoPath) 
                        ? 'bg-gradient-primary text-white' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {TOOL_NAMES[t.toolType] || t.toolType}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2 rounded-lg text-sm font-medium mb-2 ${
                isActive('/about') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              About Us
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2 rounded-lg text-sm font-medium mb-2 ${
                isActive('/contact') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Contact Us
            </Link>
            <Link
              to="/blog"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2 rounded-lg text-sm font-medium ${
                isActive('/blog') 
                  ? 'bg-gradient-primary text-white' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Blog
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;

